import { Check, Sparkles, Zap, Layers, Battery } from "lucide-react";
import { useBooking } from "@/components/site/BookingProvider";

const TIERS = [
  {
    id: "recurring",
    badge: "Lounge Membership",
    name: "Lounge — Recurring",
    price: "300",
    period: "/ month",
    blurb:
      "An auto-renewing membership at the lowest monthly rate. The full lounge, every day. Cancel anytime.",
    features: [
      "Daily PEMF + Red Light",
      "60 min HBOT — $129 (save $40)",
      "90 min HBOT — $159 (save $40)",
      "Infinity Ultra Imperial massage chair",
      "Recovery Zone + compression",
      "Herbal teas + priority booking",
    ],
    featured: true,
    cta: "Become a Member",
  },
];

const CHARGES = [
  {
    id: "quick",
    icon: Zap,
    price: "$39",
    name: "Quick Charge",
    desc: "PEMF or Red Light · single session",
  },
  {
    id: "stacked",
    icon: Layers,
    price: "$59",
    name: "Stack'd Charge",
    desc: "PEMF + Red Light · back-to-back",
  },
  {
    id: "full",
    icon: Battery,
    price: "$247",
    name: "Full Charge",
    desc: "PEMF + Red Light + 60 min HBOT",
  },
];

export default function Pricing() {
  return (
    <section
      id="membership"
      data-testid="pricing-section"
      className="relative bg-sand py-24 sm:py-32 mll-grain"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 mll-reveal">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay">
              The Lounge Menu
            </p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone leading-[1.05]">
              Anti-aging,{" "}
              <em className="italic font-normal text-jungle">redefined</em>{" "}
              through PEMF, Red Light, and Oxygen.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            <p className="text-base sm:text-lg text-muted-ink leading-relaxed">
              One quiet membership designed for daily use — because cellular
              healing rewards rhythm, not heroics. Auto-renew at the lowest
              monthly rate. For walk-ins, our &ldquo;Charge Up&rdquo; packs are
              available below.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mll-stagger items-stretch">
          {TIERS.map((t) => (
            <PricingCard key={t.id} {...t} />
          ))}
          <MembershipNote />
        </div>

        {/* HBOT a-la-carte table */}
        <div className="mt-12 sm:mt-16 mll-reveal">
          <div className="bg-bone border border-stone/10">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-stone/10">
              <div className="p-7 sm:p-9">
                <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-clay">
                  HBOT Sessions
                </p>
                <h3 className="mt-2 font-serif text-2xl text-stone">
                  Hyperbaric oxygen, by the session
                </h3>
                <p className="mt-3 text-sm text-muted-ink leading-relaxed">
                  Pay-per-visit pricing in our Oxygen Health Systems chamber.
                  Booked in person or by phone — limited daily slots.
                </p>
              </div>
              <RateRow label="60 min HBOT" member="$129" nonMember="$169" />
              <RateRow label="90 min HBOT" member="$159" nonMember="$199" />
            </div>
          </div>
        </div>

        {/* Charge Up à la carte */}
        <div className="mt-12 sm:mt-16 mll-reveal">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay">
                Non-member · Charge Up
              </p>
              <h3 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-stone">
                Walk-in by the session.
              </h3>
            </div>
            <p className="text-sm text-muted-ink max-w-md">
              No membership required. Three escalating &ldquo;charge&rdquo;
              packs for the curious — book one and decide afterwards.
            </p>
          </div>

          <div
            data-testid="charge-up-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6"
          >
            {CHARGES.map((c) => (
              <ChargeCard key={c.id} {...c} />
            ))}
          </div>

          <p className="mt-6 text-xs text-muted-ink italic">
            Ask about our VIP membership that includes FuzionFit access.
          </p>
        </div>

        {/* Packages — prepaid bundles */}
        <div className="mt-12 sm:mt-16 mll-reveal">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay">
                Prepaid · Best Value
              </p>
              <h3 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-stone">
                Packages.
              </h3>
            </div>
            <p className="text-sm text-muted-ink max-w-md">
              Buy a stack, save on every visit. Stacked sessions combine PEMF
              and Red Light back-to-back. Use them at your own pace.
            </p>
          </div>

          <div
            data-testid="packages-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-px bg-stone/10 border border-stone/10"
          >
            <PackageCard
              id="pack-5-stacked"
              count="5"
              title="Stacked"
              subtitle="PEMF or Red Light"
              price="$150"
              perSession="$30 / session"
              note="Save $145 vs walk-in"
            />
            <PackageCard
              id="pack-10-stacked"
              count="10"
              title="Stacked"
              subtitle="PEMF or Red Light"
              price="$250"
              perSession="$25 / session"
              note="Save $340 vs walk-in"
              featured
            />
            <PackageCard
              id="pack-10-hbot"
              count="10"
              title="HBOT"
              subtitle="90-minute sessions"
              price="$1,500"
              perSession="$150 / session"
              note="Save $490 vs drop-in"
            />
          </div>

          <p className="mt-4 text-xs text-muted-ink italic">
            Packages don&rsquo;t expire while you&rsquo;re an active member.
            Reserve in person, by phone, or via the Mindbody App.
          </p>
        </div>
      </div>
    </section>
  );
}

function PricingCard({ id, badge, name, price, period, blurb, features, featured, cta }) {
  const { openIntake } = useBooking();
  return (
    <article
      data-testid={`pricing-tier-${id}`}
      className={`relative p-8 sm:p-12 border transition-colors ${
        featured
          ? "bg-jungle text-bone border-jungle"
          : "bg-bone text-stone border-stone/10"
      }`}
    >
      {featured && (
        <div className="absolute -top-4 left-8 inline-flex items-center gap-1.5 bg-clay text-bone px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold">
          <Sparkles className="w-3 h-3" />
          {badge}
        </div>
      )}

      <p
        className={`text-[11px] uppercase tracking-[0.28em] font-semibold ${
          featured ? "text-bone/70" : "text-clay"
        }`}
      >
        {!featured ? badge : "Auto-Renew · Cancel Anytime"}
      </p>

      <h3 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">
        {name}
      </h3>

      <div className="mt-6 flex items-end gap-2">
        <span className="font-serif text-6xl sm:text-7xl leading-none">
          ${price}
        </span>
        <span
          className={`pb-2 text-sm ${
            featured ? "text-bone/70" : "text-muted-ink"
          }`}
        >
          {period}
        </span>
      </div>

      <p
        className={`mt-5 text-[15px] leading-relaxed max-w-md ${
          featured ? "text-bone/85" : "text-muted-ink"
        }`}
      >
        {blurb}
      </p>

      <ul className="mt-8 space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <Check
              className={`w-4 h-4 mt-0.5 shrink-0 ${
                featured ? "text-clay-soft" : "text-jungle"
              }`}
            />
            <span className={featured ? "text-bone/90" : "text-stone/85"}>
              {f}
            </span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={openIntake}
        data-testid={`pricing-cta-${id}`}
        className={`mt-10 inline-flex w-full items-center justify-center h-14 text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors ${
          featured
            ? "bg-bone text-jungle hover:bg-clay-soft hover:text-bone"
            : "bg-jungle text-bone hover:bg-jungle-dark"
        }`}
      >
        {cta}
      </button>
    </article>
  );
}

function RateRow({ label, member, nonMember }) {
  return (
    <div className="p-7 sm:p-9 flex flex-col justify-center">
      <p className="font-serif text-xl text-stone">{label}</p>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-clay">
            Member
          </p>
          <p className="mt-1 font-serif text-2xl text-stone">{member}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-ink">
            Drop-in
          </p>
          <p className="mt-1 font-serif text-2xl text-stone/70">{nonMember}</p>
        </div>
      </div>
    </div>
  );
}

function ChargeCard({ id, icon: Icon, price, name, desc }) {
  return (
    <div
      data-testid={`charge-card-${id}`}
      className="bg-bone border border-stone/10 p-7 hover:border-jungle transition-colors group"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-jungle/30 text-jungle group-hover:bg-jungle group-hover:text-bone transition-colors">
          <Icon className="w-4 h-4" />
        </span>
        <span className="font-serif text-3xl text-stone">{price}</span>
      </div>
      <h4 className="mt-6 font-serif text-2xl text-stone">{name}</h4>
      <p className="mt-2 text-sm text-muted-ink leading-relaxed">{desc}</p>
    </div>
  );
}

function PackageCard({ id, count, title, subtitle, price, perSession, note, featured }) {
  return (
    <article
      data-testid={`package-${id}`}
      className={`relative p-7 sm:p-9 transition-colors ${
        featured ? "bg-jungle text-bone" : "bg-bone text-stone hover:bg-bone"
      }`}
    >
      {featured && (
        <span className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.22em] font-semibold bg-clay text-bone px-2 py-1">
          Best Value
        </span>
      )}
      <div className="flex items-baseline gap-3">
        <span
          className={`font-serif text-5xl sm:text-6xl leading-none ${
            featured ? "text-bone" : "text-stone"
          }`}
        >
          {count}
        </span>
        <span
          className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${
            featured ? "text-bone/70" : "text-clay"
          }`}
        >
          Pack
        </span>
      </div>
      <h4
        className={`mt-5 font-serif text-2xl sm:text-3xl tracking-tight ${
          featured ? "text-bone" : "text-stone"
        }`}
      >
        {title}
      </h4>
      <p
        className={`mt-1 text-sm ${
          featured ? "text-bone/75" : "text-muted-ink"
        }`}
      >
        {subtitle}
      </p>
      <div
        className={`mt-8 pt-6 border-t flex items-end justify-between ${
          featured ? "border-bone/15" : "border-stone/10"
        }`}
      >
        <div>
          <p className="font-serif text-3xl sm:text-4xl">{price}</p>
          <p
            className={`text-xs mt-1 ${
              featured ? "text-bone/70" : "text-muted-ink"
            }`}
          >
            {perSession}
          </p>
        </div>
        <p
          className={`text-[10px] uppercase tracking-[0.22em] font-semibold text-right max-w-[120px] ${
            featured ? "text-clay-soft" : "text-clay"
          }`}
        >
          {note}
        </p>
      </div>
    </article>
  );
}

function MembershipNote() {
  return (
    <aside
      data-testid="membership-note"
      className="bg-bone border border-stone/10 p-8 sm:p-12 flex flex-col justify-between"
    >
      <div>
        <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay">
          What's inside
        </p>
        <h3 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-stone leading-[1.1]">
          The full lounge,{" "}
          <em className="italic font-normal text-jungle">every day.</em>
        </h3>
        <p className="mt-5 text-[15px] leading-relaxed text-muted-ink">
          One quiet membership, six modalities. Pulse XL PEMF, the PlatinumLED
          booth, the Oxygen Health Systems chamber, an Infinity Ultra Imperial
          massage chair, the Recovery Zone, and a daily pour of herbal tea.
        </p>
      </div>
      <ul className="mt-8 space-y-3 text-sm text-stone/85">
        {[
          "PEMF — daily",
          "Red Light — daily",
          "Hyperbaric — member-discounted",
          "Massage Chair — anytime",
          "Recovery Zone — anytime",
          "Herbal teas — on us",
        ].map((line) => (
          <li key={line} className="flex items-start gap-3">
            <span className="mt-2 w-1 h-1 rounded-full bg-clay shrink-0" />
            {line}
          </li>
        ))}
      </ul>
      <p className="mt-8 text-xs text-muted-ink italic">
        Ask about our VIP membership that includes FuzionFit access.
      </p>
    </aside>
  );
}
